// Internationalization utilities

// Currency mappings by country/region
const CURRENCY_MAP = {
  'IE': { code: 'EUR', symbol: '€' },
  'GB': { code: 'GBP', symbol: '£' },
  'US': { code: 'USD', symbol: '$' },
  'CA': { code: 'CAD', symbol: 'C$' },
  'FR': { code: 'EUR', symbol: '€' },
  'DE': { code: 'EUR', symbol: '€' },
  'ES': { code: 'EUR', symbol: '€' },
  'IT': { code: 'EUR', symbol: '€' },
  'NL': { code: 'EUR', symbol: '€' },
  'BE': { code: 'EUR', symbol: '€' },
  'AT': { code: 'EUR', symbol: '€' },
  'PT': { code: 'EUR', symbol: '€' },
  'CH': { code: 'CHF', symbol: 'Fr' },
  'DK': { code: 'DKK', symbol: 'kr' },
  'SE': { code: 'SEK', symbol: 'kr' },
  'NO': { code: 'NOK', symbol: 'kr' },
  'PL': { code: 'PLN', symbol: 'zł' },
  'CZ': { code: 'CZK', symbol: 'Kč' },
  'HU': { code: 'HUF', symbol: 'Ft' },
  'AU': { code: 'AUD', symbol: 'A$' },
  'NZ': { code: 'NZD', symbol: 'NZ$' },
  'SG': { code: 'SGD', symbol: 'S$' },
  'JP': { code: 'JPY', symbol: '¥' },
  'KR': { code: 'KRW', symbol: '₩' },
  'CN': { code: 'CNY', symbol: '¥' },
  'IN': { code: 'INR', symbol: '₹' },
  'BR': { code: 'BRL', symbol: 'R$' },
  'MX': { code: 'MXN', symbol: '$' },
  'AR': { code: 'ARS', symbol: '$' },
  'ZA': { code: 'ZAR', symbol: 'R' },
  'default': { code: 'EUR', symbol: '€' }
};

// Language mappings by country/browser
const LANGUAGE_MAP = {
  'IE': 'en-IE',
  'GB': 'en-IE', // UK users get Irish English but with UK flag
  'US': 'en-US',
  'CA': 'en-US',
  'AU': 'en-US',
  'NZ': 'en-US',
  'ZA': 'en-US',
  'FR': 'fr',
  'BE': 'fr', // Default to French for Belgium
  'CH': 'fr', // Default to French for Switzerland
  'DE': 'de',
  'AT': 'de',
  'ES': 'es',
  'MX': 'es',
  'AR': 'es',
  'default': 'en-IE' // Default to Irish English
};

// Available languages
const AVAILABLE_LANGUAGES = ['en-IE', 'en-US', 'ga', 'fr', 'de', 'es'];

// Detect user's country from browser-provided hints (no external calls)
export const detectCountry = async () => {
  try {
    // Prefer explicit region from browser language preferences
    const langs = (navigator.languages && navigator.languages.length)
      ? navigator.languages
      : [navigator.language].filter(Boolean);
    for (const l of langs) {
      if (typeof l === 'string' && l.includes('-')) {
        const parts = l.split('-');
        const region = parts[1] || '';
        if (region) return region.toUpperCase();
      }
    }

    // Try the resolved locale (may include region for some browsers)
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    if (locale && locale.includes('-')) {
      const region = locale.split('-')[1];
      if (region) return region.toUpperCase();
    }

    // As a weak hint, map some common time zones to country codes
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const TZ_TO_COUNTRY = {
      'Europe/Dublin': 'IE',
      'Europe/London': 'GB',
      'America/New_York': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'America/Los_Angeles': 'US',
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'Europe/Madrid': 'ES',
      'Europe/Rome': 'IT',
    };
    if (tz && TZ_TO_COUNTRY[tz]) {
      return TZ_TO_COUNTRY[tz];
    }
  } catch (error) {
    console.warn('Country detection fallback:', error);
  }

  // Default
  return 'IE';
};

// Detect user's preferred language
export const detectLanguage = (countryCode) => {
  // First check browser language preference
  const browserLang = navigator.language || navigator.languages[0];
  if (browserLang) {
    const langCode = browserLang.split('-')[0].toLowerCase();
    const regionCode = browserLang.split('-')[1]?.toUpperCase();
    
    // Handle English variants specifically
    if (langCode === 'en') {
      if (regionCode === 'US' || regionCode === 'CA') {
        return 'en-US';
      }
      // Default to Irish English for other English variants
      return 'en-IE';
    }
    
    if (AVAILABLE_LANGUAGES.includes(langCode)) {
      return langCode;
    }
  }

  // Fallback to country-based language mapping
  return LANGUAGE_MAP[countryCode] || LANGUAGE_MAP.default;
};

// Get currency for country
export const getCurrencyForCountry = (countryCode) => {
  return CURRENCY_MAP[countryCode] || CURRENCY_MAP.default;
};

// Format price with proper decimals and currency
export const formatPrice = (price, currency) => {
  const hasDecimals = price % 1 !== 0;
  const formattedPrice = hasDecimals ? price.toFixed(2) : price.toString();
  return `${currency.symbol}${formattedPrice}`;
};

// Load translation file
export const loadTranslations = async (language) => {
  try {
    const translations = await import(`../locales/${language}.json`);
    return translations.default;
  } catch (error) {
    console.warn(`Failed to load translations for ${language}, falling back to Irish English`);
    const fallback = await import('../locales/en-IE.json');
    return fallback.default;
  }
};

// Simple translation function with nested object support
export const translate = (translations, key, params = {}) => {
  const keys = key.split('.');
  let value = translations;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return key; // Return key if translation not found
    }
  }

  // Render and normalize string values
  if (typeof value === 'string') {
    let rendered = value;

    // Replace parameters in the translation
    if (Object.keys(params).length > 0) {
      rendered = rendered.replace(/\{(\w+)\}/g, (match, param) => {
        return params[param] !== undefined ? params[param] : match;
      });
    }

    // Normalize prohibited punctuation: replace em-dash (—) with spaced hyphen
    rendered = rendered.replace(/—/g, ' - ');

    return rendered;
  }

  return value;
}; 
